// Sprint 7 — Marketplace API schemas.
//
// Single source of truth for the wire contract between backend
// (Fastify route validation), mobile (typed API client) and web
// (Vite/React API client).
import { z } from 'zod';
// ─── Enums (mirror Prisma) ──────────────────────────────────────────────────
export const MarketplaceSaleModeSchema = z.enum(['FIXED', 'AUCTION']);
export const MarketplaceSaleStatusSchema = z.enum([
    'LISTED',
    'OFFER_PLACED',
    // Phase 25 iter-12 — auction closed; assigned winner has 24h to /reserve.
    'AUCTION_WON',
    'RESERVED',
    'PAYMENT_HELD',
    'CONTRACT_PENDING',
    'CONFIRMED',
    'IN_TRANSIT',
    'DELIVERED',
    'COMPLETED',
    'CANCELLED',
    'DISPUTED',
    'RESOLVED',
]);
export const DeliveryModeSchema = z.enum(['FARM_PICKUP', 'BUYER_DELIVERY', 'SILLEDA_TRANSFER']);
export const VatRegimeSchema = z.enum([
    'NONE',
    'PARTICULAR',
    'AUTONOMO_REAGP',
    'AUTONOMO_GENERAL',
    'EMPRESA',
]);
// ─── Phase 11 — Compliance Nivel A: seller attestations ─────────────────────
//
// The four `z.literal(true)` declarations the seller must accept before an
// animal can be listed on the marketplace. They mirror the four clauses of
// the Documenso v2 compraventa contract (PRD phase 11 §C). `z.literal(true)`
// rejects both `false` and a missing key — the handler still writes the four
// timestamps server-side (it never trusts a client-supplied date).
export const MarketplaceAttestationsSchema = z.object({
    /** Cláusula 1 — seller figures in REGA/TRACES as the animal's owner. */
    attestation_rega_owner: z.literal(true),
    /** Cláusula 2 — farm has no active sanitary restrictions / withdrawal periods. */
    attestation_no_sanitary_restrictions: z.literal(true),
    /** Cláusula 3 — seller commits to issuing the SITRAN guide within 48h of CONFIRMED. */
    attestation_sitran_commitment: z.literal(true),
    /** Cláusula 4 — seller assumes full AEAT (fiscal) + Xunta (sanitary) responsibility. */
    attestation_aeat_responsibility: z.literal(true),
});
// TTL (seconds) for the pre-signed PUT URLs of marketplace compliance PDFs
// (DIB + SITRAN). 10 minutes — a slower upload budget than animal photos
// because the PDFs can be larger and are picked from the device file system.
export const MARKETPLACE_DOC_UPLOAD_URL_TTL_SECONDS = 600;
// ─── List an animal for sale ────────────────────────────────────────────────
export const CreateSaleRequestSchema = z
    .object({
    sale_mode: MarketplaceSaleModeSchema,
    price_cents: z.number().int().positive(),
    delivery_mode: DeliveryModeSchema,
    auction_starts_at: z.string().datetime().optional(),
    auction_ends_at: z.string().datetime().optional(),
    min_bid_increment_cents: z.number().int().positive().default(5000),
    // Phase 11 — the four seller attestations are mandatory on listing.
    attestation_rega_owner: z.literal(true),
    attestation_no_sanitary_restrictions: z.literal(true),
    attestation_sitran_commitment: z.literal(true),
    attestation_aeat_responsibility: z.literal(true),
    // Lote V-7 Fase B — transporte + transmisión do risco para o
    // contrato profesional (cláusula 4). Todos opcionais; se non se
    // declaran, o contrato renderiza fallback supletorio do Código Civil.
    max_delivery_date: z.string().datetime({ offset: true }).optional(),
    transport_responsible: z.enum(['SELLER', 'BUYER', 'THIRD_PARTY']).optional(),
    risk_transfer_moment: z.enum(['PICKUP', 'DELIVERY']).optional(),
})
    .refine((data) => data.sale_mode === 'FIXED' || (data.auction_starts_at && data.auction_ends_at), { message: 'auction_starts_at + auction_ends_at required when sale_mode=AUCTION' });
// Backfill body for `PATCH /marketplace/sales/:id/attestations` — lets a
// seller complete the four attestations on a sale that was LISTED before the
// phase 11 migration landed (PRD phase 11 §B.2).
export const MarketplaceAttestationsRequestSchema = MarketplaceAttestationsSchema;
// ─── Sale summary (catalog / list views) ────────────────────────────────────
export const MarketplaceSaleSummarySchema = z.object({
    id: z.string(),
    animal: z.object({
        id: z.string(),
        crotal_id: z.string(),
        // Lote V-2 (F-023) — exposto no envelope para que o detail marketplace
        // poida pintar o QR público (mesmo formato que ve o consumidor na
        // carnicería) e o enlace `chain.leyro.es/qr/<qr_code>`. Sen este campo
        // o comprador non pode escanear/copiar antes de pagar.
        qr_code: z.string(),
        name: z.string().nullable(),
        breed: z.string().nullable(),
        sex: z.string().nullable(),
        birth_date: z.string().datetime().nullable(),
        photo_url: z.string().url().nullable(),
        destination: z.enum(['BEEF', 'DAIRY', 'BREEDING']),
    }),
    seller: z.object({
        id: z.string(),
        name: z.string(),
        farm_name: z.string().nullable(),
        farm_municipality: z.string().nullable(),
        /** True when the seller's KYC is VERIFIED — surfaces the "Granxa verificada" badge. */
        verified: z.boolean(),
    }),
    sale_mode: MarketplaceSaleModeSchema,
    price_cents: z.number().int().nonnegative(),
    final_price_cents: z.number().int().nonnegative().nullable(),
    /** Current top bid for AUCTION sales (null for FIXED). */
    top_bid_cents: z.number().int().nonnegative().nullable(),
    auction_ends_at: z.string().datetime().nullable(),
    status: MarketplaceSaleStatusSchema,
    delivery_mode: DeliveryModeSchema,
    created_at: z.string().datetime(),
    // Lote V-3 followup — exposto tamén no summary para que /me/purchases
    // poida ofrecer "Descargar contrato" sen ter que pedir o detail. Null
    // ata que Documenso sela o documento + chega o webhook.
    contract_url: z.string().url().nullable(),
    // Phase 27 (B-04 fix) — exposto no summary para que o seller-side EUDR
    // dashboard saiba se a venda xa ten DDS publicada sen ter que pedir un
    // GET /eudr/dds por venda. Null cando aínda non se xerou.
    dds_reference_number: z.string().nullable(),
    // Phase 27 (B-10 fix) — expostos no summary para que /me/purchases e
    // /me/sales poidan ofrecer "Descargar DIB" + "Descargar SITRAN" sen ter
    // que pedir o detail. Null para sales LISTED no catálogo público (o
    // vendedor sobeas tras a reserva, non antes).
    dib_pdf_url: z.string().nullable(),
    sitran_pdf_url: z.string().nullable(),
});
export const MarketplaceSearchResponseSchema = z.object({
    results: z.array(MarketplaceSaleSummarySchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
});
// ─── Sale detail (single record with bids) ──────────────────────────────────
export const MarketplaceBidSchema = z.object({
    id: z.string(),
    bidder_id: z.string(),
    bidder_name: z.string(),
    amount_cents: z.number().int().nonnegative(),
    created_at: z.string().datetime(),
});
export const MarketplaceSaleDetailSchema = MarketplaceSaleSummarySchema.extend({
    buyer_id: z.string().nullable(),
    documenso_doc_id: z.string().nullable(),
    contract_signed_at: z.string().datetime().nullable(),
    // contract_url xa herda de MarketplaceSaleSummarySchema (Lote V-3 followup).
    // Lote V-3 followup (F-035) — sinaturas individuais. `contract_signed_at`
    // só se setea cando AMBOS sinaron + Documenso completou; estes campos
    // distingen quen sinou primeiro.
    buyer_signed_at: z.string().datetime().nullable(),
    seller_signed_at: z.string().datetime().nullable(),
    delivered_at: z.string().datetime().nullable(),
    sitran_guide_number: z.string().nullable(),
    sitran_emitted_at: z.string().datetime().nullable(),
    dispute_opened_at: z.string().datetime().nullable(),
    dispute_reason: z.string().nullable(),
    /** Minimum bid increment in cents (AUCTION sales). */
    min_bid_increment_cents: z.number().int().positive(),
    bids: z.array(MarketplaceBidSchema),
    /** Commission split (filled from CONFIRMED onwards). */
    platform_fee_seller_cents: z.number().int().nonnegative().nullable(),
    platform_fee_buyer_cents: z.number().int().nonnegative().nullable(),
    // ─── Phase 11 — Compliance Nivel A ──────────────────────────────────────
    // The four attestation timestamps are written server-side at listing time
    // (or via the backfill PATCH). `null` means "not attested" — the state
    // machine treats any NULL as blocking `reserve`. The two PDF URLs are
    // canonical S3 URLs, written after the client confirms the direct upload.
    rega_attestation_at: z.string().datetime().nullable(),
    sanitary_declaration_at: z.string().datetime().nullable(),
    sitran_commitment_at: z.string().datetime().nullable(),
    aeat_responsibility_at: z.string().datetime().nullable(),
    dib_pdf_url: z.string().url().nullable(),
    sitran_pdf_url: z.string().url().nullable(),
});
// ─── Bids ───────────────────────────────────────────────────────────────────
export const PlaceBidRequestSchema = z.object({
    amount_cents: z.number().int().positive(),
});
// ─── Reserve / checkout ────────────────────────────────────────────────────
export const ReserveSaleRequestSchema = z.object({
    delivery_address_line1: z.string().min(1).optional(),
    delivery_address_city: z.string().min(1).optional(),
    delivery_address_postal: z.string().min(1).optional(),
});
export const CheckoutIntentResponseSchema = z.object({
    payment_intent_id: z.string(),
    client_secret: z.string(),
    /** Total amount the buyer is being charged, in cents (price + buyer commission). */
    amount_cents: z.number().int().positive(),
});
// ─── Sitran guide ──────────────────────────────────────────────────────────
export const SitranGuideRequestSchema = z.object({
    guide_number: z.string().min(1),
});
// ─── Phase 11 — Compliance PDF uploads (DIB + SITRAN guide PDF) ─────────────
//
// `POST /marketplace/sales/:id/dib` (and `…/sitran-pdf`) accept a single
// `{ content_length }` body and return a pre-signed PUT URL the client uploads
// the PDF to directly, plus the canonical S3 URL. The client then calls the
// matching `…-complete` PATCH with that canonical URL so the route can
// HEAD-verify the object and persist `dib_pdf_url` / `sitran_pdf_url`.
// Phase 13 M6 — Hard upload cap for compliance PDFs. 25 MB lets a heavy
// notarised DIB scan in colour fit while still bounding bucket abuse. The
// presigned PUT URL is signed with `ContentLength: content_length`, so S3
// rejects mismatched bodies with SignatureDoesNotMatch / EntityTooLarge.
export const MARKETPLACE_DOC_UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
/** `POST /marketplace/sales/:id/dib` (and sibling) body — the client declares
 *  the exact PDF byte count so the backend can sign with `ContentLength`. */
export const MarketplaceDocUploadRequestSchema = z
    .object({
    content_length: z.number().int().positive().max(MARKETPLACE_DOC_UPLOAD_MAX_BYTES),
})
    .strict();
export const MarketplaceDocUploadResponseSchema = z.object({
    /** Pre-signed S3 PUT URL — content-type must be `application/pdf`. */
    upload_url: z.string().url(),
    /** Stable public URL persisted onto the sale once the upload completes. */
    canonical_url: z.string().url(),
    expires_at: z.string().datetime({ offset: true }),
    /** Mirrors `MARKETPLACE_DOC_UPLOAD_MAX_BYTES` for client UX (banner before picker). */
    max_bytes: z.number().int().positive(),
});
export const MarketplaceDocCompleteRequestSchema = z.object({
    /** The canonical URL returned by the matching upload-URL endpoint. */
    canonical_url: z.string().url(),
});
// ─── Dispute ───────────────────────────────────────────────────────────────
export const DisputeRequestSchema = z.object({
    reason: z.string().min(10).max(2000),
});
// V1.5 — Resolución admin da disputa.
export const DisputeResolutionSchema = z.enum(['BUYER', 'SELLER']);
export const DisputeResolveRequestSchema = z.object({
    resolution: DisputeResolutionSchema,
    notes: z.string().min(3).max(2000),
});
export const DisputeResolveResponseSchema = z.object({
    sale_id: z.string(),
    status: z.string(),
    refunded: z.boolean(),
    rectificatives_emitted: z.number().int().nonnegative(),
});
// V1.5 — Listado admin de disputas (abertas + resoltas).
export const AdminDisputeItemSchema = z.object({
    sale_id: z.string(),
    animal_label: z.string(),
    status: z.string(),
    buyer_name: z.string().nullable(),
    seller_name: z.string(),
    price_cents: z.number().int(),
    dispute_opened_at: z.string().datetime({ offset: true }).nullable(),
    dispute_reason: z.string().nullable(),
    dispute_resolved_at: z.string().datetime({ offset: true }).nullable(),
    dispute_resolution: z.string().nullable(),
    // Phase 27 (B-11 fix) — admin debe poder descargar a documentación
    // sanitaria (DIB + guía SITRAN) para responder a requirimentos AEAT,
    // SITRAN, ou solicitudes do operador. Presignadas (TTL curto) polo
    // backend antes de devolver.
    dib_pdf_url: z.string().nullable(),
    sitran_pdf_url: z.string().nullable(),
});
export const AdminDisputesListResponseSchema = z.object({
    open: z.array(AdminDisputeItemSchema),
    resolved: z.array(AdminDisputeItemSchema),
});
// ─── Phase 12 M8 — BUYER first-purchase confirmation (SEPA + REGA + VAT) ────
//
// Lands the per-sale fields needed for the Documenso compraventa template
// (`dest_rega_code` on the sale, fiscal identity on the buyer's User row) AND
// the persistent buyer-side fields needed for downstream Stripe SEPA debits
// (mandate id + IBAN last 4 on the BUYER's UserRoleAssignment). Triggered
// once per buyer's first purchase; subsequent purchases reuse the mandate
// but still write `dest_rega_code` per sale (the destination can vary).
/** IBAN: a permissive shape (ES + 22 digits is the canonical Spanish form,
 *  but we accept any 14-34 ASCII chars to cover EU IBANs). The mod-97
 *  checksum is enforced server-side via `validateIban()`; here we only
 *  reject obviously malformed strings. */
const IbanSchema = z
    .string()
    .min(14)
    .max(34)
    .regex(/^[A-Z0-9 ]+$/i, 'IBAN must be alphanumeric');
/** REGA code: 14-char Spanish holding identifier (ES + 12 digits). The full
 *  catalogue lives on the Xunta side; here we accept the format only. */
const RegaCodeSchema = z.string().regex(/^ES\d{12}$/, 'REGA code must match ES + 12 digits');
export const BuyerConfirmRequestSchema = z
    .object({
    iban: IbanSchema,
    dest_rega_code: RegaCodeSchema,
    vat_regime: VatRegimeSchema,
    entity_type: z.enum(['PERSOA_FISICA', 'PERSOA_XURIDICA']),
    legal_name: z.string().min(1).max(160).optional(),
    fiscal_address: z.string().min(1).max(240),
})
    .strict()
    .refine((v) => v.entity_type === 'PERSOA_FISICA' || !!v.legal_name, {
    message: 'legal_name is required when entity_type is PERSOA_XURIDICA',
    path: ['legal_name'],
});
/** Response carries the masked IBAN + a flag for whether a real Stripe
 *  mandate was created. When Stripe is unconfigured the route still writes
 *  `buyer_confirmed_at` + the IBAN last 4 (no mandate id) so the BUYER can
 *  proceed; downstream `reserve` re-checks before debiting. */
export const BuyerConfirmResponseSchema = z.object({
    sale_id: z.string().uuid(),
    buyer_confirmed_at: z.string().datetime({ offset: true }),
    sepa_iban_last4: z.string().regex(/^\d{4}$/),
    sepa_mandate_id: z.string().nullable(),
    dest_rega_code: z.string(),
});
// ─── Stripe Connect onboarding ─────────────────────────────────────────────
export const ConnectStatusResponseSchema = z.object({
    has_account: z.boolean(),
    details_submitted: z.boolean(),
    charges_enabled: z.boolean(),
    payouts_enabled: z.boolean(),
    bank_verified: z.boolean(),
    /** Stripe-side `requirements.currently_due` array (empty when complete). */
    requirements: z.array(z.string()),
    /**
     * Phase 25 iter-10 — true when the caller owns at least one animal with a
     * LeyroSpot NFC tag bound (`Animal.leyrospot_uuid IS NOT NULL`). Surfaced
     * here so the web SellerSetupCard can render the full vendor checklist in
     * a single round trip instead of issuing a second `/animals` query.
     */
    has_leyrospot_animal: z.boolean(),
    /**
     * Phase 25 iter-11 — Farm summary lookup. The JWT never carries `farm_id`
     * (it would force a re-mint on farm create), so the web profile mirrors
     * the truth via this field. `has_farm` is the boolean gate; `farm_summary`
     * carries enough to render the vendor checklist line without a second
     * `/farms/me` round trip. Both are null/false until the gandeiro creates
     * the Farm (typically during mobile onboarding).
     */
    has_farm: z.boolean(),
    farm_summary: z
        .object({
        id: z.string().uuid(),
        name: z.string(),
        municipality: z.string(),
    })
        .nullable(),
});
export const ConnectOnboardResponseSchema = z.object({
    onboarding_url: z.string().url(),
});
// ─── Inline route-only response shapes ─────────────────────────────────────
// Promoted to shared/* so the contract-audit drift sentinel doesn't flag
// the marketplace.ts routes as containing local z.object literals.
export const MarketplaceSearchQuerySchema = z.object({
    q: z.string().optional(),
    breed: z.string().optional(),
    destination: z.enum(['BEEF', 'DAIRY', 'BREEDING']).optional(),
    verified_only: z.coerce.boolean().optional(),
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const BidPlacedResponseSchema = z.object({
    ok: z.boolean(),
    top_bid_cents: z.number().int(),
});
export const SigningUrlResponseSchema = z.object({
    url: z.string().url().nullable(),
    signed: z.boolean(),
});
export const MarketplaceSaleListResponseSchema = z.object({
    results: z.array(MarketplaceSaleSummarySchema),
});
// Lote V-3 (F-030) — real Stripe PaymentIntent status surfaced to the
// buyer's checkout SPA. `authorised: true` means money is committed
// (card → requires_capture; SEPA → processing; finalised → succeeded);
// false means the buyer never completed the PaymentElement.
export const PaymentStatusResponseSchema = z.object({
    sale_id: z.string(),
    sale_status: z.string(),
    payment_intent_id: z.string().nullable(),
    payment_intent_status: z.string().nullable(),
    authorised: z.boolean(),
});
//# sourceMappingURL=marketplace.js.map